import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ── 모바일 감지 훅 ──────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

// ── 색상 토큰 (메인화면과 동일) ──────────────────────────────
const BLUE    = '#3a8fd4';
const BLUE_BG = '#e6f1fb';

// ── 아이콘 ───────────────────────────────────────────────────
const Ico = {
  Cal:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Team: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Chart:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>,
};

function CardHeader({ icon, title }) {
  return (
    <div style={S.cardHeader}>
      <div style={S.cardIcon}>{icon}</div>
      <h2 style={S.cardTitle}>{title}</h2>
    </div>
  );
}

function PlannerPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const userStored  = localStorage.getItem('currentUser');
  const currentUser = userStored ? JSON.parse(userStored) : { name: '사용자' };

  const [currentDate,           setCurrentDate]           = useState(new Date());
  const [showInputModal,        setShowInputModal]        = useState(false);
  const [showDoneModal,         setShowDoneModal]         = useState(false);
  const [showDetailModal,       setShowDetailModal]       = useState(false);
  const [showTaskDetailModal,   setShowTaskDetailModal]   = useState(false);
  const [showTeamProjectModal,  setShowTeamProjectModal]  = useState(false);
  const [showLogoutModal,       setShowLogoutModal]       = useState(false);

  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [selectedEvent,   setSelectedEvent]   = useState(null);
  const [contextMenu,     setContextMenu]     = useState({ visible: false, x: 0, y: 0, dateKey: null });
  const [isEditing,       setIsEditing]       = useState(false);

  const [title,        setTitle]        = useState('');
  const [period,       setPeriod]       = useState('');
  const [detail,       setDetail]       = useState('');
  const [category,     setCategory]     = useState('과제');
  const [newTaskText,  setNewTaskText]  = useState('');
  const [memberInput,  setMemberInput]  = useState('');

  const [calendarEvents,  setCalendarEvents]  = useState({});
  const [assignments,     setAssignments]     = useState([]);
  const [doneAssignments, setDoneAssignments] = useState([]);
  const [teamProjects,    setTeamProjects]    = useState([]);

  const apiUrl = process.env.REACT_APP_API_URL || 'https://campus-life-production.up.railway.app';

  const saveToLocal = (events) => localStorage.setItem('aiSavedSession', JSON.stringify(events));

  useEffect(() => {
    const userId   = JSON.parse(localStorage.getItem('currentUser') || '{}')?.userId || '2';
    const localRaw = localStorage.getItem('aiSavedSession');
    const localData = localRaw ? JSON.parse(localRaw) : {};

    fetch(`${apiUrl}/api/schedules/${userId}`)
      .then(res => res.json())
      .then(data => {
        const backendData = {};
        data.forEach(schedule => {
          const rawDate = schedule.dueDate.split('T')[0];
          const [y, m, d] = rawDate.split('-').map(Number);
          const key = `${y}-${m}-${d}`;
          if (!backendData[key]) backendData[key] = [];
          backendData[key].push({
            id: schedule.id,
            type: schedule.type === 'EXAM' ? '시험' : schedule.type === 'ASSIGNMENT' ? '과제' : '팀플',
            title: schedule.title,
            period: schedule.dueDate,
            detail: '',
            progress: schedule.isDone ? 100 : 0,
            doneTasks: [],
            todoTasks: [],
          });
        });

        const merged = { ...backendData };
        Object.keys(localData).forEach(date => {
          if (!merged[date]) {
            merged[date] = localData[date];
          } else {
            merged[date] = merged[date].map(ev => {
              const localEv = localData[date]?.find(l => l.id === ev.id);
              if (localEv) return { ...ev, todoTasks: localEv.todoTasks || [], doneTasks: localEv.doneTasks || [], detail: localEv.detail || ev.detail };
              return ev;
            });
            const backendIds = new Set(merged[date].map(e => e.id));
            const localOnly  = localData[date].filter(e => !backendIds.has(e.id));
            merged[date] = [...merged[date], ...localOnly];
          }
        });

        setCalendarEvents(merged);
        saveToLocal(merged);
      })
      .catch(() => { if (Object.keys(localData).length > 0) setCalendarEvents(localData); });
  }, [apiUrl]);

  useEffect(() => {
    const viewYear  = currentDate.getFullYear();
    const viewMonth = currentDate.getMonth() + 1;
    const updated   = { ...calendarEvents };
    let changed = false;

    Object.entries(updated).forEach(([key, evList]) => {
      evList.forEach((ev, idx) => {
        if (ev.type === '과제' || ev.type === '팀플') {
          const total       = (ev.todoTasks?.length || 0) + (ev.doneTasks?.length || 0);
          const calcProgress = total === 0 ? 0 : Math.round((ev.doneTasks.length / total) * 100);
          if (ev.progress !== calcProgress) { updated[key][idx].progress = calcProgress; changed = true; }
        }
      });
    });

    if (changed) { setCalendarEvents(updated); return; }

    const monthValues = Object.entries(calendarEvents)
      .filter(([key]) => { const [y, m] = key.split('-').map(Number); return y === viewYear && m === viewMonth; })
      .flatMap(([, val]) => val);

    setAssignments(monthValues.filter(ev => ev.type === '과제' && ev.progress < 100));
    setTeamProjects(monthValues.filter(ev => ev.type === '팀플' && ev.progress < 100));
    setDoneAssignments(monthValues.filter(ev => (ev.type === '과제' || ev.type === '팀플') && ev.progress === 100));
  }, [calendarEvents, currentDate]);

  const handleTaskAction = (eventId, task, action) => {
    let updatedEvent = { ...selectedEvent };
    if (action === 'add') {
      if (newTaskText.trim() !== '') { updatedEvent.todoTasks = [...(updatedEvent.todoTasks || []), newTaskText.trim()]; setNewTaskText(''); }
    } else if (action === 'complete') {
      updatedEvent.todoTasks = updatedEvent.todoTasks.filter(t => t !== task);
      updatedEvent.doneTasks = [...(updatedEvent.doneTasks || []), task];
    } else if (action === 'undo') {
      updatedEvent.doneTasks = updatedEvent.doneTasks.filter(t => t !== task);
      updatedEvent.todoTasks = [...(updatedEvent.todoTasks || []), task];
    } else if (action === 'delete') {
      updatedEvent.todoTasks = updatedEvent.todoTasks.filter(t => t !== task);
      updatedEvent.doneTasks = updatedEvent.doneTasks.filter(t => t !== task);
    }
    setSelectedEvent(updatedEvent);
  };

  const handleSave = () => {
    if (!title) return;
    const userId  = JSON.parse(localStorage.getItem('currentUser') || '{}')?.userId || '2';
    const typeMap = { '과제': 'ASSIGNMENT', '시험': 'EXAM', '팀플': 'PROJECT' };
    const dateKey = period
      ? `${period.split('-')[0]}-${parseInt(period.split('-')[1])}-${parseInt(period.split('-')[2])}`
      : selectedDateKey;
    const dueDateStr = period
      ? `${period}T09:00:00`
      : `${dateKey.split('-')[0]}-${String(dateKey.split('-')[1]).padStart(2,'0')}-${String(dateKey.split('-')[2]).padStart(2,'0')}T09:00:00`;

    if (isEditing) {
      const newEvent = { ...selectedEvent, type: category, title, period, detail, members: category === '팀플' ? (memberInput ? memberInput.split(',').map(m => m.trim()) : [currentUser.name]) : [] };
      const updatedDayEvents = (calendarEvents[dateKey] || []).map(ev => ev.id === newEvent.id ? newEvent : ev);
      const newEventsToSave  = { ...calendarEvents, [dateKey]: updatedDayEvents };
      setCalendarEvents(newEventsToSave); saveToLocal(newEventsToSave); closeModal();
    } else {
      fetch(`${apiUrl}/api/schedules/${userId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, type: typeMap[category], dueDate: dueDateStr }),
      })
      .then(res => res.json())
      .then(saved => {
        const newEvent = { id: saved.id || Date.now(), type: category, title, period: dueDateStr, detail, progress: 0, doneTasks: [], todoTasks: [], members: category === '팀플' ? (memberInput ? memberInput.split(',').map(m => m.trim()) : [currentUser.name]) : [] };
        const newEventsToSave = { ...calendarEvents, [dateKey]: [...(calendarEvents[dateKey] || []), newEvent] };
        setCalendarEvents(newEventsToSave); saveToLocal(newEventsToSave);
      })
      .catch(() => {
        const newEvent = { id: Date.now(), type: category, title, period: dueDateStr, detail, progress: 0, doneTasks: [], todoTasks: [], members: category === '팀플' ? (memberInput ? memberInput.split(',').map(m => m.trim()) : [currentUser.name]) : [] };
        const newEventsToSave = { ...calendarEvents, [dateKey]: [...(calendarEvents[dateKey] || []), newEvent] };
        setCalendarEvents(newEventsToSave); saveToLocal(newEventsToSave);
      });
      closeModal();
    }
  };

  const handleTaskSave = () => {
    const dateKey = Object.keys(calendarEvents).find(k => calendarEvents[k].some(ev => ev.id === selectedEvent.id));
    const updatedDayEvents = calendarEvents[dateKey].map(ev => ev.id === selectedEvent.id ? selectedEvent : ev);
    const newEventsToSave  = { ...calendarEvents, [dateKey]: updatedDayEvents };
    setCalendarEvents(newEventsToSave); saveToLocal(newEventsToSave); closeModal();
  };

  const handleDelete = (dateKey, eventId) => {
    fetch(`${apiUrl}/api/schedules/${eventId}`, { method: 'DELETE' }).catch(() => {});
    const updatedDayEvents = calendarEvents[dateKey].filter(ev => ev.id !== eventId);
    const newEvents = { ...calendarEvents };
    if (updatedDayEvents.length === 0) delete newEvents[dateKey]; else newEvents[dateKey] = updatedDayEvents;
    setCalendarEvents(newEvents); saveToLocal(newEvents);
    setContextMenu({ visible: false, x: 0, y: 0, dateKey: null, eventId: null });
  };

  const closeModal = () => {
    setShowInputModal(false); setShowDoneModal(false); setShowDetailModal(false);
    setShowTaskDetailModal(false); setShowTeamProjectModal(false); setShowLogoutModal(false);
    setIsEditing(false); setTitle(''); setPeriod(''); setDetail(''); setSelectedEvent(null);
    setSelectedDateKey(null); setContextMenu({ visible: false, x: 0, y: 0, dateKey: null, eventId: null });
    setMemberInput('');
  };

  const confirmLogout = () => { localStorage.removeItem('currentUser'); navigate('/'); };
  const changeMonth   = (offset) => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));

  const { firstDay, days, year, month } = (() => {
    const y = currentDate.getFullYear();
    const m = currentDate.getMonth();
    return { firstDay: new Date(y, m, 1).getDay(), days: new Date(y, m + 1, 0).getDate(), year: y, month: m + 1 };
  })();

  const today = new Date();

  const eventTagStyle = (type) => ({
    ...S.eventTag,
    backgroundColor: type === '시험' ? '#fee2e2' : type === '팀플' ? '#dbeafe' : '#dcfce7',
    color:           type === '시험' ? '#b91c1c' : type === '팀플' ? '#1e40af' : '#166534',
  });

  const gaugeColor = (progress) => progress <= 50 ? '#faad14' : BLUE;

  return (
    <div style={{ ...S.root, overflowX: 'hidden' }} onClick={() => setContextMenu({ ...contextMenu, visible: false })}>
      <main style={{
        ...S.main,
        // 모바일: 패딩 줄이기
        padding: isMobile ? '16px 14px 60px' : '32px 32px 60px',
        gap: isMobile ? '12px' : '16px',
      }}>

        {/* ── AI 알림 바 ── */}
        {assignments.filter(a => a.progress < 50).length > 0 && (
          <div style={S.alertBar}>
            ⚠️ <strong>AI 우선 분석:</strong> 현재 {month}월 과제 중 진척도가 낮은 항목이 {assignments.filter(a => a.progress < 50).length}개 있습니다!
          </div>
        )}

        {/* ── 학습 팁 + AI 지원 버튼 (모바일: 세로 배치) ── */}
        <div style={{
          ...S.tipRow,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
        }}>
          <div style={S.tipBox}>
            💡 오늘의 학습 팁: {(() => {
              if (assignments.length === 0) return '오늘도 화이팅! 일정을 추가해보세요 😊';
              const todayDate = new Date();
              todayDate.setHours(0, 0, 0, 0);
              const sorted = [...assignments]
                .filter(a => {
                  const date = new Date(a.period ? a.period.split('T')[0] : '9999-12-31');
                  date.setHours(0, 0, 0, 0);
                  return date >= todayDate;
                })
                .sort((a, b) => {
                  const dateA = new Date(a.period ? a.period.split('T')[0] : '9999-12-31');
                  const dateB = new Date(b.period ? b.period.split('T')[0] : '9999-12-31');
                  return dateA - dateB;
                });
              if (sorted.length === 0) return '오늘도 화이팅! 일정을 추가해보세요 😊';
              return `"${sorted[0].title}" 과제를 먼저 완료해보세요!`;
            })()}
          </div>
          <button style={{
            ...S.aiBtn,
            width: isMobile ? '100%' : 'auto',
          }} onClick={() => navigate('/ai-assistant')}>🤖 AI 학습 지원</button>
        </div>

        {/* ── 메인 그리드 (모바일: 1열) ── */}
        <div style={{
          ...S.contentGrid,
          gridTemplateColumns: isMobile ? '1fr' : '1.8fr 1fr',
          gap: isMobile ? '12px' : '20px',
        }}>

          {/* ── 캘린더 카드 ── */}
          <div style={S.card}>
            <CardHeader icon={Ico.Cal} title="캘린더" />
            <div style={S.calNav}>
              <button style={S.calNavBtn} onClick={() => changeMonth(-1)}>◀</button>
              <h3 style={S.calNavTitle}>{year}년 {month}월</h3>
              <button style={S.calNavBtn} onClick={() => changeMonth(1)}>▶</button>
            </div>
            <div style={{ ...S.calGrid, gap: isMobile ? '1px' : '4px' }}>
              {['일','월','화','수','목','금','토'].map((d, i) => (
                <div key={d} style={{ ...S.calDayLabel, color: i === 0 ? '#ef4444' : i === 6 ? BLUE : '#94a3b8' }}>{d}</div>
              ))}
              {Array.from({ length: 42 }, (_, i) => {
                const dayNum         = i - firstDay + 1;
                const isCurrentMonth = dayNum > 0 && dayNum <= days;
                if (i >= 35 && !isCurrentMonth) return null;
                const dateKey = `${year}-${month}-${dayNum}`;
                const events  = calendarEvents[dateKey] || [];
                const isToday = isCurrentMonth && dayNum === today.getDate() && month === today.getMonth() + 1 && year === today.getFullYear();

                return (
                  <div
                    key={i}
                    style={{
                      ...S.dayBox,
                      minHeight: isMobile ? '44px' : '72px',
                      height: isMobile ? '44px' : '72px',   // 고정 높이 — 칸이 늘어나지 않음
                      padding: isMobile ? '2px' : '5px',
                      opacity: isCurrentMonth ? 1 : 0.3,
                      overflow: 'hidden',
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                    onClick={() => {
                      if (!isCurrentMonth) return;
                      setSelectedDateKey(dateKey);
                      if (events.length === 0) {
                        // 일정 없는 날: 바로 추가 모달
                        setCategory('과제'); setShowInputModal(true);
                      } else if (isMobile) {
                        // 모바일 + 일정 있는 날: 추가 모달 열기
                        setCategory('과제'); setShowInputModal(true);
                      }
                      // 데스크탑 + 일정 있는 날: 기존대로 아무 동작 없음 (우클릭 메뉴 사용)
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (!isCurrentMonth) return;
                      setContextMenu({ visible: true, x: e.pageX, y: e.pageY, dateKey });
                    }}
                  >
                    <span style={{ ...S.dayNum, ...(isToday ? S.dayNumToday : {}), fontSize: isMobile ? '10px' : '11px' }}>
                      {isCurrentMonth ? dayNum : ''}
                    </span>
                    <div
                      className="cal-events-scroll"
                      style={{
                      overflowY: 'auto',
                      flex: 1,
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                    }}>
                      {isCurrentMonth && events.map(event => (
                        <div
                          key={event.id}
                          style={{
                            ...eventTagStyle(event.type),
                            // 항상 넘침 방지 — 데스크탑에서 추가한 긴 제목도 잘림
                            fontSize: isMobile ? '8px' : '10px',
                            padding: isMobile ? '1px 2px' : '2px 5px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '100%',
                            display: 'block',
                            boxSizing: 'border-box',
                          }}
                          onClick={(e) => { e.stopPropagation(); setSelectedDateKey(dateKey); setSelectedEvent(event); setShowDetailModal(true); }}
                          onContextMenu={(e) => { e.stopPropagation(); e.preventDefault(); setContextMenu({ visible: true, x: e.pageX, y: e.pageY, dateKey, eventId: event.id }); }}
                        >
                          {event.title}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── 사이드 영역 ── */}
          <div style={S.sideArea}>

            {/* 팀 프로젝트 카드 */}
            <div style={S.card}>
              <CardHeader icon={Ico.Team} title={`${month}월 팀 프로젝트`} />
              {teamProjects.length > 0 ? teamProjects.map((tp, idx) => (
                <div key={tp.id} style={{ marginBottom: idx < teamProjects.length - 1 ? '18px' : 0 }}>
                  <div style={S.itemRow}>
                    <span style={S.itemTitle}>{tp.title}</span>
                    <button style={S.miniBtn} onClick={(e) => { e.stopPropagation(); setSelectedEvent(tp); setShowTaskDetailModal(true); }}>수정</button>
                  </div>
                  <div style={S.gaugeTrack}>
                    <div style={{ ...S.gaugeFill, width: `${tp.progress}%`, backgroundColor: gaugeColor(tp.progress) }} />
                  </div>
                  <div style={S.gaugeLabel}>
                    <span>진행률</span>
                    <span style={{ color: gaugeColor(tp.progress), fontWeight: '600' }}>{tp.progress}%</span>
                  </div>
                  {idx < teamProjects.length - 1 && <div style={S.divider} />}
                </div>
              )) : <p style={S.emptyText}>팀 프로젝트가 없습니다.</p>}
            </div>

            {/* 과제 진척도 카드 */}
            <div style={S.card}>
              <CardHeader icon={Ico.Chart} title={`${month}월 과제 진척도`} />
              {assignments.length > 0 ? assignments.map((item, idx) => (
                <div key={item.id} style={{ marginBottom: idx < assignments.length - 1 ? '18px' : 0 }}>
                  <div style={S.itemRow}>
                    <span style={S.itemTitle}>{item.title}</span>
                    <button style={S.miniBtn} onClick={(e) => { e.stopPropagation(); setSelectedEvent(item); setShowTaskDetailModal(true); }}>수정</button>
                  </div>
                  <div style={S.gaugeTrack}>
                    <div style={{ ...S.gaugeFill, width: `${item.progress}%`, backgroundColor: gaugeColor(item.progress) }} />
                  </div>
                  <div style={S.gaugeLabel}>
                    <span>진행률</span>
                    <span style={{ color: gaugeColor(item.progress), fontWeight: '600' }}>{item.progress}%</span>
                  </div>
                  {idx < assignments.length - 1 && <div style={S.divider} />}
                </div>
              )) : <p style={S.emptyText}>진행 중인 과제가 없습니다.</p>}
            </div>

            {/* 완료 과제함 버튼 */}
            <button style={S.doneBtn} onClick={() => setShowDoneModal(true)}>✅ {month}월 완료 과제함</button>
          </div>
        </div>
      </main>

      {/* ── 컨텍스트 메뉴 ── */}
      {contextMenu.visible && (
        <div style={{ ...S.contextMenu, top: contextMenu.y, left: contextMenu.x }}>
          <div style={S.contextItem} onClick={() => { setSelectedDateKey(contextMenu.dateKey); setIsEditing(false); setTitle(''); setPeriod(''); setDetail(''); setCategory('과제'); setShowInputModal(true); }}>➕ 일정 추가</div>
          {contextMenu.eventId && (
            <>
              <div style={S.contextItem} onClick={() => {
                const ev = calendarEvents[contextMenu.dateKey].find(e => e.id === contextMenu.eventId);
                setSelectedEvent(ev); setSelectedDateKey(contextMenu.dateKey);
                setTitle(ev.title); setPeriod(ev.period); setDetail(ev.detail); setCategory(ev.type);
                setMemberInput(ev.members ? ev.members.join(', ') : '');
                setIsEditing(true); setShowInputModal(true);
              }}>✏️ 내용 수정</div>
              <div style={S.contextItem} onClick={() => { setSelectedEvent(calendarEvents[contextMenu.dateKey].find(e => e.id === contextMenu.eventId)); setShowDetailModal(true); }}>🔍 상세 보기</div>
              <div style={{ ...S.contextItem, color: '#ef4444' }} onClick={() => handleDelete(contextMenu.dateKey, contextMenu.eventId)}>🗑️ 삭제하기</div>
            </>
          )}
        </div>
      )}

      {/* ── 상세 보기 모달 ── */}
      {showDetailModal && selectedEvent && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>{selectedEvent.title}</h2>
              <span style={S.categoryTag(selectedEvent.type)}>{selectedEvent.type}</span>
            </div>
            <div style={S.infoRow}>📅 <strong>마감일:</strong> {selectedEvent.period ? selectedEvent.period.split('T')[0] : '날짜 미지정'}</div>
            {selectedEvent.detail && <div style={S.infoRow}>📝 <strong>내용:</strong> {selectedEvent.detail}</div>}
            {(selectedEvent.type === '과제' || selectedEvent.type === '팀플') && (
              <div style={S.progressBox}>
                <div style={{ marginBottom: '12px', fontWeight: '600', fontSize: '13px', color: '#1e293b' }}>📈 현재 진행 상황</div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#ef4444', fontWeight: '600' }}>🚀 진행 중인 일: </span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    {selectedEvent.doneTasks?.includes(selectedEvent.title) ? '모두 완료!' : selectedEvent.title}
                  </span>
                </div>
                <div>
                  <span style={{ fontSize: '12px', color: '#22c55e', fontWeight: '600' }}>✅ 완료된 일: </span>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    {selectedEvent.doneTasks?.includes(selectedEvent.title) ? selectedEvent.title : '없음'}
                  </span>
                </div>
              </div>
            )}
            <div style={S.modalBtnRow}>
              <button style={S.primaryBtn} onClick={() => { setShowDetailModal(false); setShowInputModal(true); setIsEditing(true); setTitle(selectedEvent.title); setPeriod(selectedEvent.period); setDetail(selectedEvent.detail); setCategory(selectedEvent.type); setMemberInput(selectedEvent.members?.join(', ') || ''); }}>수정</button>
              <button style={S.secondaryBtn} onClick={closeModal}>닫기</button>
            </div>
            {/* 모바일 전용: 삭제 버튼 */}
            {isMobile && (
              <button
                style={{ ...S.primaryBtn, width: '100%', marginTop: '10px', backgroundColor: '#ef4444', border: 'none' }}
                onClick={() => {
                  const dateKey = Object.keys(calendarEvents).find(k =>
                    calendarEvents[k].some(e => e.id === selectedEvent.id)
                  );
                  if (dateKey) { handleDelete(dateKey, selectedEvent.id); closeModal(); }
                }}
              >
                🗑️ 삭제하기
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── 할 일 관리 모달 ── */}
      {(showTaskDetailModal || showTeamProjectModal) && selectedEvent && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, borderTop: `4px solid ${BLUE}` }}>
            <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>{selectedEvent.title}</h3>
            <div style={S.taskBox}>
              <p style={{ fontWeight: '700', fontSize: '12px', color: '#64748b', marginBottom: '10px' }}>🚀 진행 중인 일</p>
              {selectedEvent.doneTasks?.includes(selectedEvent.title) ? (
                <p style={{ fontSize: '12px', color: '#94a3b8', padding: '6px 0' }}>진행 중인 할 일이 없어요!</p>
              ) : (
                <div style={S.taskRow}>
                  <span style={{ flex: 1, fontSize: '13px', color: '#374151' }}>• {selectedEvent.title}</span>
                  <button style={S.completeBtn} onClick={() => { let u = { ...selectedEvent }; u.doneTasks = [...(u.doneTasks || []), u.title]; setSelectedEvent(u); }}>✅ 완료</button>
                </div>
              )}
              <p style={{ fontWeight: '700', fontSize: '12px', color: '#22c55e', marginTop: '16px', marginBottom: '10px' }}>✅ 완료된 일</p>
              {selectedEvent.doneTasks?.includes(selectedEvent.title) ? (
                <div style={S.taskRow}>
                  <span style={{ textDecoration: 'line-through', color: '#94a3b8', flex: 1, fontSize: '13px' }}>• {selectedEvent.title}</span>
                  <button style={S.undoBtn} onClick={() => { let u = { ...selectedEvent }; u.doneTasks = u.doneTasks.filter(t => t !== u.title); setSelectedEvent(u); }}>↩ 되돌리기</button>
                </div>
              ) : (
                <p style={{ fontSize: '12px', color: '#94a3b8', padding: '6px 0' }}>아직 완료된 항목이 없어요!</p>
              )}
            </div>
            <div style={S.modalBtnRow}>
              <button style={S.primaryBtn} onClick={handleTaskSave}>저장하기</button>
              <button style={S.secondaryBtn} onClick={closeModal}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 완료 과제함 모달 ── */}
      {showDoneModal && (
        <div style={S.modalOverlay}>
          <div style={{ ...S.modal, textAlign: 'center' }}>
            <h2 style={{ color: '#22c55e', marginBottom: '16px', fontSize: '18px', fontWeight: '700' }}>🏆 {month}월 완료 과제함</h2>
            {doneAssignments.length > 0 ? doneAssignments.map(as => (
              <div key={as.id} style={S.doneItem}>
                <span>🎉 {as.title}</span>
                <button style={S.miniBtn} onClick={() => { setSelectedEvent(as); setShowTaskDetailModal(true); setShowDoneModal(false); }}>관리</button>
              </div>
            )) : <p style={{ padding: '20px', color: '#94a3b8', fontSize: '13px' }}>아직 완료된 과제가 없습니다.</p>}
            <button style={{ ...S.primaryBtn, marginTop: '16px', width: '100%', backgroundColor: '#22c55e' }} onClick={closeModal}>확인</button>
          </div>
        </div>
      )}

      {/* ── 일정 추가/수정 모달 ── */}
      {showInputModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
              일정 {isEditing ? '수정' : '추가'}
            </h3>
            <select style={S.input} value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="과제">과제</option>
              <option value="시험">시험</option>
              <option value="팀플">팀플</option>
            </select>
            <div style={{ position: 'relative', marginBottom: '10px' }}>
              <input
                style={{ ...S.input, marginBottom: 0 }}
                value={title}
                onChange={(e) => { if (e.target.value.length <= 20) setTitle(e.target.value); }}
                placeholder="일정 이름 (최대 20자)"
                maxLength={20}
              />
              <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: title.length >= 20 ? '#ef4444' : '#94a3b8' }}>
                {title.length}/20
              </span>
            </div>
            <div style={{ position: 'relative', marginBottom: '10px' }}>
              <textarea
                style={{ ...S.input, height: '80px', resize: 'vertical', marginBottom: 0 }}
                value={detail}
                onChange={(e) => { if (e.target.value.length <= 200) setDetail(e.target.value); }}
                placeholder="상세 내용을 입력하세요 (최대 200자)"
                maxLength={200}
              />
              <span style={{ position: 'absolute', right: '12px', bottom: '10px', fontSize: '11px', color: detail.length >= 200 ? '#ef4444' : '#94a3b8' }}>
                {detail.length}/200
              </span>
            </div>
            <div style={S.modalBtnRow}>
              <button style={S.primaryBtn} onClick={handleSave}>저장하기</button>
              <button style={S.secondaryBtn} onClick={closeModal}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 로그아웃 모달 ── */}
      {showLogoutModal && (
        <div style={S.modalOverlay}>
          <div style={S.modal}>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '24px' }}>로그아웃하시겠습니까?</p>
            <div style={S.modalBtnRow}>
              <button style={S.primaryBtn} onClick={confirmLogout}>확인</button>
              <button style={S.secondaryBtn} onClick={() => setShowLogoutModal(false)}>취소</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 스타일 (원본 그대로 — 레이아웃 조정은 JSX 인라인으로) ────
const S = {
  root: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    minHeight: '100vh', backgroundColor: '#f8fafc',
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', sans-serif",
  },
  main: {
    width: '100%', maxWidth: '1100px',
    padding: '32px 32px 60px', boxSizing: 'border-box',
    display: 'flex', flexDirection: 'column', gap: '16px',
  },
  alertBar: {
    backgroundColor: '#fff2f0', color: '#b91c1c',
    padding: '11px 16px', borderRadius: '10px',
    border: '1px solid #fecaca', fontSize: '13px', fontWeight: '600',
    textAlign: 'center',
  },
  tipRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' },
  tipBox: {
    flex: 1, backgroundColor: '#fff', padding: '13px 18px',
    borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#374151',
    border: '1px solid #f1f5f9',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  aiBtn: {
    padding: '11px 18px', backgroundColor: BLUE, color: '#fff',
    border: 'none', borderRadius: '10px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  contentGrid: { display: 'grid', gridTemplateColumns: '1.8fr 1fr', gap: '20px' },
  card: {
    backgroundColor: '#fff', borderRadius: '14px', padding: '22px 22px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)',
    border: '1px solid #f1f5f9', boxSizing: 'border-box', minWidth: 0,
  },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' },
  cardIcon: {
    width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    backgroundColor: BLUE_BG, color: BLUE,
  },
  cardTitle: { margin: 0, fontSize: '14px', fontWeight: '600', color: '#1e293b' },
  calNav:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  calNavBtn:   { background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: BLUE, fontWeight: '800' },
  calNavTitle: { fontSize: '14px', fontWeight: '700', color: '#1e293b', margin: 0 },
  calGrid:     { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' },
  calDayLabel: { textAlign: 'center', fontSize: '11px', fontWeight: '600', padding: '3px 0 6px' },
  dayBox: {
    minHeight: '72px', border: '1px solid #f1f5f9', borderRadius: '8px',
    padding: '5px', cursor: 'pointer', position: 'relative', boxSizing: 'border-box',
  },
  dayNum: { fontSize: '11px', fontWeight: '600', color: '#94a3b8', display: 'inline-block' },
  dayNumToday: {
    width: '20px', height: '20px', borderRadius: '50%',
    backgroundColor: BLUE, color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '11px', fontWeight: '700',
  },
  eventTag: {
    marginTop: '2px', padding: '2px 5px', borderRadius: '4px',
    fontSize: '10px', fontWeight: '700',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    cursor: 'pointer', display: 'block', boxSizing: 'border-box', maxWidth: '100%',
  },
  sideArea: { display: 'flex', flexDirection: 'column', gap: '20px' },
  itemRow:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' },
  itemTitle:{ fontSize: '13px', fontWeight: '600', color: '#1e293b' },
  miniBtn:  { padding: '3px 9px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '5px', cursor: 'pointer', color: '#374151' },
  gaugeTrack: { width: '100%', height: '6px', backgroundColor: '#e2e8f0', borderRadius: '99px', overflow: 'hidden' },
  gaugeFill:  { height: '100%', borderRadius: '99px', transition: 'width 0.5s' },
  gaugeLabel: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#94a3b8', marginTop: '3px' },
  divider:   { height: '1px', backgroundColor: '#f1f5f9', margin: '18px 0' },
  emptyText: { fontSize: '12px', color: '#94a3b8', textAlign: 'center', padding: '10px 0', margin: 0 },
  doneBtn: {
    width: '100%', padding: '12px',
    backgroundColor: '#fff', color: BLUE,
    border: `1.5px solid ${BLUE}`, borderRadius: '10px',
    fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxSizing: 'border-box',
  },
  contextMenu: { position: 'absolute', backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', boxShadow: '0 8px 20px rgba(0,0,0,0.12)', zIndex: 4000, padding: '6px 0', minWidth: '140px' },
  contextItem: { padding: '10px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: '600', color: '#374151' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 3000 },
  modal: { backgroundColor: '#fff', padding: '28px 32px', borderRadius: '14px', boxShadow: '0 20px 40px rgba(0,0,0,0.12)', width: '90%', maxWidth: '420px', boxSizing: 'border-box' },
  modalBtnRow: { display: 'flex', gap: '10px', marginTop: '20px' },
  primaryBtn:  { flex: 1, padding: '12px', backgroundColor: BLUE, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  secondaryBtn:{ flex: 1, padding: '12px', backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
  input: { width: '100%', padding: '11px 13px', marginBottom: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box', color: '#374151', backgroundColor: '#f8fafc', outline: 'none' },
  infoRow: { marginBottom: '10px', fontSize: '13px', color: '#374151' },
  progressBox: { marginTop: '14px', padding: '14px 16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' },
  categoryTag: (type) => ({
    padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: '700',
    backgroundColor: type === '시험' ? '#fee2e2' : type === '팀플' ? '#dbeafe' : '#dcfce7',
    color:           type === '시험' ? '#b91c1c' : type === '팀플' ? '#1e40af' : '#166534',
  }),
  taskBox:     { backgroundColor: '#f8fafc', padding: '14px 16px', borderRadius: '10px', border: '1px solid #e2e8f0' },
  taskRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #e2e8f0', gap: '8px' },
  completeBtn: { padding: '3px 9px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '5px', cursor: 'pointer', flexShrink: 0 },
  undoBtn:     { padding: '3px 9px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: '5px', cursor: 'pointer', flexShrink: 0 },
  doneItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', backgroundColor: '#f0fdf4', borderRadius: '8px', marginBottom: '8px', border: '1px solid #bbf7d0', fontWeight: '600', color: '#166534', fontSize: '13px' },
};

// 캘린더 이벤트 칸 스크롤바 숨김
if (typeof document !== 'undefined' && !document.getElementById('cal-scroll-style')) {
  const style = document.createElement('style');
  style.id = 'cal-scroll-style';
  style.textContent = '.cal-events-scroll::-webkit-scrollbar { display: none; }';
  document.head.appendChild(style);
}

export default PlannerPage;